import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HrnbRow, HrnbCol } from '../../../components/controls/HrnbContainer';
import HrnbCard from '../../../components/controls/HrnbCard';
import HrnbButton from '../../../components/controls/HrnbButton';
import HrnbAlert from '../../../components/controls/HrnbAlert';
import HrnbLoader from '../../../components/controls/HrnbLoader';
import HrnbSkeleton from '../../../components/controls/HrnbSkeleton';
import { BackIcon, JoinIcon, RejectIcon, ParticipantsIcon, EditIcon, PlusIcon, DeleteIcon } from '../../../icons';
import {
  TYPE_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
  VISIBILITY_LABELS,
  FORMAT_LABELS,
} from '../constants';
import { isRegistrationOpen } from '../utils';

import '../../../css/pages/BattleDetailPage.css';

/**
 * Вкладка "Информация" страницы баттла
 */
const BattleInfoTab = ({
  battle,
  coverData,
  coverLoading,
  participants,
  rounds,
  currentUser,
  isUserOrganizer,
  isRegistered,
  actionLoading,
  deleteLoading,
  onRegister,
  onUnregister,
  onDelete,
  onEditBattle,
  navigateToParticipants,
  setActiveTab,
  onOpenInviteModal,
  onOpenRoundModal,
}) => {
  const navigate = useNavigate();

  if (!battle) {
    return (
      <HrnbAlert variant="danger">
        Баттл не найден
        <HrnbButton onClick={() => navigate('/battles')} className="mt-3">
          <BackIcon style={{ width: 16, height: 16, marginRight: 8 }} />
          Вернуться к списку
        </HrnbButton>
      </HrnbAlert>
    );
  }

  const {
    title,
    description,
    type,
    status,
    format,
    visibility,
    startTime,
    endTime,
    registrationDeadline,
    prizePool,
    maxParticipants,
    organizers = [],
    createdAt,
    tags = [],
    coverImageId,
  } = battle;

  const coverUrl = coverData?.coverUrl || null;
  const statusColor = STATUS_COLORS[status] || '#6c757d';
  const typeLabel = TYPE_LABELS[type] || type || 'Не указано';
  const statusLabel = STATUS_LABELS[status] || status || 'Не указано';
  const visibilityLabel =
    VISIBILITY_LABELS[visibility] || visibility || 'Не указано';
  const formatLabel = FORMAT_LABELS[format] || format || 'Не указан';

  return (
    <>
      {coverLoading && (
        <div className="battle-detail-cover battle-detail-cover--loading">
          <HrnbLoader size="lg" />
        </div>
      )}

      {coverUrl && !coverLoading && (
        <div className="battle-detail-cover">
          <img src={coverUrl} alt={title} className="battle-detail-cover-image" />
        </div>
      )}

      {!coverLoading && !coverUrl && coverImageId && (
        <div className="battle-detail-cover battle-detail-cover--error">
          <p className="text-muted">Не удалось загрузить обложку</p>
        </div>
      )}

      <HrnbRow>
        <HrnbCol xs={12} lg={8}>
          <HrnbCard title="О баттле" className="battle-detail-card">
            <div className="battle-detail-section">
              {description ? (
                <div className="battle-detail-description">{description}</div>
              ) : (
                <p className="text-muted">Описание отсутствует</p>
              )}

              {tags.length > 0 && (
                <div className="battle-detail-tags">
                  {tags.map((tag, idx) => (
                    <span key={idx} className="battle-detail-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="battle-detail-section">
              <h3 className="battle-detail-section-title">Детали</h3>
              <div className="battle-detail-grid">
                <InfoItem label="Тип:" value={typeLabel} />
                <InfoItem label="Формат:" value={
                  <span className={`battle-detail-format-badge battle-detail-format-badge--${(format || '').toLowerCase()}`}>
                    {formatLabel}
                  </span>
                } />
                <InfoItem label="Видимость:" value={visibilityLabel} />
                <InfoItem label="Начало:" value={formatDate(startTime)} />
                <InfoItem label="Окончание:" value={formatDate(endTime)} />
                {prizePool && (
                  <InfoItem label="Призовой фонд:" value={prizePool} />
                )}
                {maxParticipants && (
                  <InfoItem
                    label="Макс. участников:"
                    value={maxParticipants}
                  />
                )}
                <InfoItem
                  label="Участников:"
                  value={participants.length || 0}
                />
                {registrationDeadline && (
                  <InfoItem
                    label="Дедлайн регистрации:"
                    value={formatDate(registrationDeadline)}
                  />
                )}
                {createdAt && (
                  <InfoItem label="Создан:" value={formatDate(createdAt)} />
                )}
              </div>
            </div>

            {organizers.length > 0 && (
              <div className="battle-detail-section">
                <h3 className="battle-detail-section-title">Организаторы</h3>
                <ul className="battle-detail-list">
                  {organizers.map((o, idx) => (
                    <li key={idx} className="battle-detail-list-item">
                      {o.name || o.username || o}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="battle-detail-actions">
              {currentUser &&
                isRegistrationOpen(status) &&
                (isRegistered ? (
                  <HrnbButton
                    variant="danger"
                    onClick={onUnregister}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <HrnbLoader size="sm" />
                    ) : (
                      <>
                        <RejectIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                        Отменить регистрацию
                      </>
                    )}
                  </HrnbButton>
                ) : (
                  <HrnbButton
                    variant="primary"
                    onClick={onRegister}
                    className="battle-detail-join-btn"
                  >
                    <JoinIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                    Зарегистрироваться
                  </HrnbButton>
                ))}

              <HrnbButton
                variant="outline-primary"
                onClick={navigateToParticipants}
              >
                <ParticipantsIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                Участники ({participants.length})
              </HrnbButton>

              {currentUser && isUserOrganizer && (
                <>
                  <HrnbButton
                    variant="outline-primary"
                    onClick={onEditBattle}
                    className="battle-detail-edit-btn"
                  >
                    <EditIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                    Редактировать баттл
                  </HrnbButton>
                  <HrnbButton
                    variant="success"
                    outline
                    onClick={onOpenInviteModal}
                    className="battle-detail-invite-btn"
                  >
                    <PlusIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                    Пригласить
                  </HrnbButton>
                  <HrnbButton
                    variant="danger"
                    outline
                    onClick={onDelete}
                    disabled={deleteLoading}
                    className="battle-detail-delete-btn"
                  >
                    {deleteLoading ? (
                      <HrnbLoader size="sm" />
                    ) : (
                      <>
                        <DeleteIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                        Удалить баттл
                      </>
                    )}
                  </HrnbButton>
                </>
              )}
            </div>
          </HrnbCard>
        </HrnbCol>

        <HrnbCol xs={12} lg={4}>
          {rounds.length > 0 && (
            <HrnbCard title="Раунды" className="battle-detail-card battle-detail-card--dark">
              <div className="battle-rounds-list">
                {rounds.map((round) => (
                  <div
                    key={round.id}
                    className="battle-round-item battle-round-item--clickable"
                    onClick={() => onOpenRoundModal && onOpenRoundModal(round)}
                  >
                    <div className="battle-round-number">
                      Раунд {round.number}
                    </div>
                    <div className="battle-round-status">{round.status}</div>
                    <div className="battle-round-time">
                      {formatDate(round.startTime)} — {formatDate(round.endTime)}
                    </div>
                  </div>
                ))}
              </div>
            </HrnbCard>
          )}

          {participants.length > 0 && (
            <HrnbCard title="Участники" className="battle-detail-card">
              <div className="battle-participants-preview">
                {participants
                  .filter((p) => p.role === 'PARTICIPANT')
                  .slice(0, 6)
                  .map((p) => (
                    <div key={p.id} className="participant-preview-item">
                      <span className="participant-name">
                        {p.nickname || p.name || 'Участник'}
                      </span>
                      <span className="participant-role">
                        {getRoleLabel(p.role)}
                      </span>
                    </div>
                  ))}
                {participants.filter((p) => p.role === 'PARTICIPANT').length >
                  6 && (
                  <HrnbButton
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveTab('participants')}
                  >
                    <PlusIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                    +
                    {participants.filter((p) => p.role === 'PARTICIPANT')
                      .length - 6}{' '}
                    ещё
                  </HrnbButton>
                )}
              </div>
            </HrnbCard>
          )}
        </HrnbCol>
      </HrnbRow>
    </>
  );
};

/** Вспомогательный компонент */
const InfoItem = ({ label, value }) => (
  <div className="battle-detail-info-item">
    <span className="battle-detail-info-label">{label}</span>
    <span className="battle-detail-info-value">{value}</span>
  </div>
);

/** Форматирование даты */
const formatDate = (dateStr) => {
  if (!dateStr) return 'Не указано';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Получить отображение роли */
const getRoleLabel = (role) => {
  const labels = {
    ORGANIZER: 'Организатор',
    PARTICIPANT: 'Участник',
    JUDGE: 'Жюри',
  };
  return labels[role] || role;
};

export default BattleInfoTab;
